import { useCallback } from 'react';
import { useObservableEagerState } from 'observable-hooks';
import { RxCollection, RxDocument } from 'rxdb';
import {
	EMPTY,
	filter,
	firstValueFrom,
	identity,
	ignoreElements,
	interval,
	map,
	merge,
	Observable,
	startWith,
	tap,
	timeout,
	TimeoutError,
} from 'rxjs';

import { PassengerBookingView } from '@mopla/data-models';

import { useBusinessLayer } from '../business-logic';
import { useDBObservableDataContext } from '../contexts/dataContext';
import { TPassengerBookingDocType } from '../schemas/passengerBooking';

/** Thrown when a booking does not appear in RxDB within the timeout window. */
export class BookingSyncTimeoutError extends Error {
	constructor(bookingId: string) {
		super(`Timeout exceeded while waiting for booking with ID: ${bookingId}`);
		this.name = 'BookingSyncTimeoutError';
	}
}

const RESYNC_RETRY_INTERVAL_MS = 2_000;

/** Extracts and return state from user$ (from the react context) */
export const usePassengerBookingsV2 = () => {
	const { bookings$ } = useDBObservableDataContext();

	if (!bookings$) {
		throw new Error(
			'No bookings$ found in DBObservableDataContext. Refer to {withDBObservableDataContext}'
		);
	}

	return useObservableEagerState(bookings$);
};

export const usePassengerBookingsActions = () => {
	const { replications, db } = useBusinessLayer();

	const resyncBookings = useCallback(() => {
		const replicationController = replications?.['passenger_booking'];

		if (!replicationController) {
			throw new Error('No passengerBooking replicationController found.');
		}

		replicationController.reSync();
	}, [replications]);

	/** Resolves once the queried booking shows up in RxDB, or throws on timeout. */
	const waitForBooking = useCallback(
		async (
			booking$: Observable<RxDocument<TPassengerBookingDocType> | null>,
			reference: string,
			options: { timeoutDuration?: number; resyncWhileWaiting?: boolean }
		): Promise<PassengerBookingView> => {
			/**
			 * `/syncBookings` is eventually consistent — a just-created booking is sometimes absent from
			 * the next pull round, and the replication's own resync tick is too rare and unaligned to
			 * reliably land another round before the timeout. So drive extra rounds here.
			 * Side-effect only stream; unsubscribed once the booking arrives or the timeout fires.
			 * */
			const resync$ = options.resyncWhileWaiting
				? interval(RESYNC_RETRY_INTERVAL_MS).pipe(
						startWith(0),
						tap(() => resyncBookings()),
						ignoreElements()
				  )
				: EMPTY;

			return await firstValueFrom(
				merge(
					booking$.pipe(
						filter(Boolean),
						/** TODO this is a types hotfix */
						map(
							(booking) => booking.toJSON() as unknown as PassengerBookingView
						)
					),
					resync$
				).pipe(
					options.timeoutDuration ? timeout(options.timeoutDuration) : identity
				)
			).catch((err) => {
				/** Only the timeout maps to the sync error; other failures propagate as-is. */
				if (err instanceof TimeoutError) {
					throw new BookingSyncTimeoutError(reference);
				}
				throw err;
			});
		},
		[resyncBookings]
	);

	/**
	 * Waits for the passenger booking with the specified booking number to become available in the RxDB.
	 * If the booking is not found within the optional timeout duration, an error is thrown.
	 */
	const waitForPassengerBooking = useCallback(
		(
			bookingNumber: string,
			options: { timeoutDuration?: number; resyncWhileWaiting?: boolean } = {}
		): Promise<PassengerBookingView> =>
			waitForBooking(
				(
					db.collections[
						'passenger_booking'
					] as RxCollection<TPassengerBookingDocType>
				).findOne(bookingNumber).$,
				bookingNumber,
				options
			),
		[db, waitForBooking]
	);

	/** Same wait keyed by the booking uuid - the primary key is the booking number. */
	const waitForPassengerBookingById = useCallback(
		(
			bookingId: string,
			options: { timeoutDuration?: number; resyncWhileWaiting?: boolean } = {}
		): Promise<PassengerBookingView> =>
			waitForBooking(
				(
					db.collections[
						'passenger_booking'
					] as RxCollection<TPassengerBookingDocType>
				).findOne({ selector: { id: bookingId } }).$,
				bookingId,
				options
			),
		[db, waitForBooking]
	);

	return {
		resyncBookings,
		waitForPassengerBooking,
		waitForPassengerBookingById,
	};
};
